import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  InputAdornment,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
  useTheme,
  Card,
  CardContent,
  Avatar,
  useMediaQuery
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Refresh,
  Close,
  Inventory,
  Category,
  Straighten,
  Store
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import Sidebar from '../../components/sidebars/Sidebar _inventory';
import TopNav from '../../components/top_bars/TopNav';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
  borderRadius: '20px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
    borderColor: alpha(theme.palette.primary.main, 0.3),
  }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '16px',
  background: theme.palette.background.paper,
  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.05)}`,
  maxHeight: '500px',
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    fontWeight: 600,
    fontSize: '0.875rem',
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: theme.palette.background.paper,
  }
}));

const ItemNamesManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    serial_number: '',
    item_name: '',
    category: '',
    description: '',
    unit_of_measurement: ''
  });
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const unitOptions = ['pcs', 'kg', 'litres', 'Bottle' ,'bunches', 'packs', 'boxes', 'reams', 'units', 'meters', 'feet', 'Cans', 'Rolls', 'Portions', 'Loaaves','Bag', 'Hand', 'Tubers','Grams'];
  const categoryOptions = ['Kitchen Items', 'Restaurant Items','Lounge', 'Arcade', 'Beauty Products', 'Salon Supplies', 'Fashion', 'Spa Items', 'Hall', 'Office Consumables', 'Operational Consumables','Others'];

  // Fetch items
  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/inventory/get_item_names_manage.php?${params}`);
      if (response.data.success) {
        setItems(response.data.data);
        setTotalItems(response.data.total);
      } else {
        throw new Error(response.data.message || 'Failed to fetch items');
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to fetch items',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, page, rowsPerPage]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Add new item
  const handleAddItem = async () => {
    if (!formData.item_name || !formData.category || !formData.unit_of_measurement) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields',
        severity: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/inventory/add_item_name.php`, formData);
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: response.data.message || 'Item added successfully',
          severity: 'success'
        });
        handleCloseAddDialog();
        fetchItems();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error adding item:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to add item',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Edit item
  const handleEditItem = async () => {
    if (!formData.item_name || !formData.category || !formData.unit_of_measurement) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields',
        severity: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        id: selectedItem.id,
        serial_number: formData.serial_number,
        item_name: formData.item_name,
        category: formData.category,
        description: formData.description,
        unit_of_measurement: formData.unit_of_measurement
      };

      console.log('Updating item with payload:', payload);

      const response = await axios.put(`${API_BASE_URL}/inventory/update_item_name.php`, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Update response:', response.data);
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: response.data.message || 'Item updated successfully',
          severity: 'success'
        });
        handleCloseEditDialog();
        fetchItems();
      } else {
        throw new Error(response.data.message || 'Update failed');
      }
    } catch (error) {
      console.error('Error updating item - Full error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to update item',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Delete item
  const handleDeleteItem = async () => {
    setLoading(true);
    try {
      console.log('Deleting item with ID:', selectedItem.id);
      
      const response = await axios.delete(`${API_BASE_URL}/inventory/delete_item_name.php`, {
        data: { id: selectedItem.id },
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Delete response:', response.data);
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: response.data.message || 'Item deleted successfully',
          severity: 'success'
        });
        handleCloseDeleteDialog();
        fetchItems();
      } else {
        throw new Error(response.data.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Error deleting item - Full error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to delete item',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddDialog = () => {
    setFormData({
      serial_number: '',
      item_name: '',
      category: '',
      description: '',
      unit_of_measurement: ''
    });
    setAddDialogOpen(true);
  };

  const handleOpenEditDialog = (item) => {
    setSelectedItem(item);
    setFormData({
      serial_number: item.serial_number || '',
      item_name: item.item_name,
      category: item.category || '',
      description: item.description || '',
      unit_of_measurement: item.unit_of_measurement || ''
    });
    setEditDialogOpen(true);
  };

  const handleOpenDeleteDialog = (item) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleCloseAddDialog = () => setAddDialogOpen(false);
  const handleCloseEditDialog = () => setEditDialogOpen(false);
  const handleCloseDeleteDialog = () => setDeleteDialogOpen(false);

  const handlePageChange = (event, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate statistics
  const uniqueCategories = [...new Set(items.map(item => item.category).filter(Boolean))];
  const uniqueUnits = [...new Set(items.map(item => item.unit_of_measurement).filter(Boolean))];

  return (
    <Box sx={{ display: 'flex', backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box sx={{
        flexGrow: 1,
        p: 3,
        ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
        transition: 'margin 0.3s ease-in-out',
        maxWidth: '1600px',
        mx: 'auto',
        width: '100%'
      }}>
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('email');
            localStorage.removeItem('role');
            localStorage.removeItem('userName');
            window.location.href = '/login';
          }}
          onNotificationClick={() => console.log('Notifications')}
          toggleSidebar={toggleSidebar}
        />

        <Box sx={{ mt: 2 }}>
          {/* Statistics Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <StyledCard>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Total Items
                      </Typography>
                      <Typography variant="h4" fontWeight={700} mt={1}>
                        {totalItems}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                      <Inventory />
                    </Avatar>
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <StyledCard>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Categories
                      </Typography>
                      <Typography variant="h4" fontWeight={700} mt={1} color="success.main">
                        {uniqueCategories.length}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                      <Category />
                    </Avatar>
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <StyledCard>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Unit Types
                      </Typography>
                      <Typography variant="h4" fontWeight={700} mt={1} color="warning.main">
                        {uniqueUnits.length}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                      <Straighten />
                    </Avatar>
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>

          {/* Main Content Paper */}
          <Paper elevation={0} sx={{ borderRadius: '20px', overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ 
              p: 3, 
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.light, 0.02)} 100%)`,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Box>
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    <Store sx={{ mr: 1, verticalAlign: 'middle', fontSize: 28 }} />
                    Item Names Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage inventory item names, categories, and units of measurement
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleOpenAddDialog}
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                    }
                  }}
                >
                  Add New Item
                </Button>
              </Box>
            </Box>

            {/* Search Bar */}
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    placeholder="Search by item name, category, or serial number..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchQuery('')}>
                            <Close fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <IconButton onClick={fetchItems} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      <Refresh />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Table */}
            <StyledTableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: '100px' }}>S/N</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Item Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '150px' }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '120px' }}>Unit</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '120px' }}>Created At</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '120px' }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                        <Typography variant="body1" color="text.secondary">No items found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Chip 
                            label={item.serial_number || 'N/A'} 
                            size="small" 
                            variant="outlined"
                            sx={{ fontFamily: 'monospace' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={item.category} 
                            size="small" 
                            color="primary" 
                            variant="outlined" 
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={item.unit_of_measurement} 
                            size="small" 
                            variant="outlined"
                            sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.description || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(item.created_at).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton 
                              size="small" 
                              onClick={() => handleOpenEditDialog(item)}
                              sx={{ mr: 1 }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => handleOpenDeleteDialog(item)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </StyledTableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalItems}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Paper>
        </Box>
      </Box>

      {/* Add Dialog - FIXED DOM nesting */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" component="span" fontWeight={600}>
            Add New Item
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Serial Number"
                value={formData.serial_number}
                onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                placeholder="Optional: S/N from goods receiving log"
                helperText="Optional field"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Item Name"
                value={formData.item_name}
                onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
                placeholder="Enter item name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required sx={{ minWidth: 150 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  label="Category"
                >
                  {categoryOptions.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required sx={{ minWidth: 150 }}>
                <InputLabel>Unit of Measurement</InputLabel>
                <Select
                  value={formData.unit_of_measurement}
                  onChange={(e) => setFormData({ ...formData, unit_of_measurement: e.target.value })}
                  label="Unit of Measurement"
                >
                  {unitOptions.map(unit => (
                    <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional: Additional details about the item"
                helperText="Optional field"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAddItem} 
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            {loading ? <CircularProgress size={24} /> : 'Add Item'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog - FIXED DOM nesting */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" component="span" fontWeight={600}>
            Edit Item
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Serial Number"
                value={formData.serial_number}
                onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Item Name"
                value={formData.item_name}
                onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  label="Category"
                >
                  {categoryOptions.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Unit of Measurement</InputLabel>
                <Select
                  value={formData.unit_of_measurement}
                  onChange={(e) => setFormData({ ...formData, unit_of_measurement: e.target.value })}
                  label="Unit of Measurement"
                >
                  {unitOptions.map(unit => (
                    <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleEditItem} 
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            {loading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>
          <Typography variant="h6" component="span" fontWeight={600}>
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to delete <strong>"{selectedItem?.item_name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This action cannot be undone.
          </Typography>
          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Items that are already used in supplies cannot be deleted.
              You may need to remove all references to this item first.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDeleteItem} 
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ItemNamesManagement;